import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable, type SortingState } from '@tanstack/react-table'
import { useContext, useMemo, useState } from 'react'
import { Table as TableIcon } from '@/assets/icons'
import { AuthContext } from '@/context/auth-context'
import { getMemberDisplayName, kUnavailableMemberDisplayName } from '@/collaboration/utils/members'
import { SearchableText } from '@/components/searchable-text/SearchableText'
import { WorkProductTable, WorkProductTableUpdatedAt } from '@/components/work-product-table/WorkProductTable'
import { WorkProductTableStatus } from '@/components/work-product-table/WorkProductTableStatus'
import { Button } from '@/components/button/Button'
import { selectMatterMembers, selectMatterMembersInitialLoadComplete } from '@/matter/store/selectors'
import { useAppSelector } from '@/store/store-hooks'
import type { MatterTable } from '../schemas'
import TableActions from './TableActions'
import TableOriginTag from './TableOriginTag'

interface TableListRow extends MatterTable {
  creatorLabel: string | undefined
  modifiedAt: number | undefined
}
const columnHelper = createColumnHelper<TableListRow>()

export function TablesList({
  tables,
  matterId,
  search,
  isLoading,
  isError,
  onClearSearch,
  onRetry,
  onOpen,
  onCreate,
  onChanged,
}: {
  tables: MatterTable[]
  matterId: string
  search: string
  isLoading: boolean
  isError: boolean
  onClearSearch: () => void
  onRetry: () => void
  onOpen: (table: MatterTable) => void
  onCreate: () => void
  onChanged: () => void
}) {
  const { user } = useContext(AuthContext)
  const members = useAppSelector((state) => selectMatterMembers(state, matterId))
  const membersLoaded = useAppSelector((state) => selectMatterMembersInitialLoadComplete(state, matterId))
  const [sorting, setSorting] = useState<SortingState>([{ id: 'modifiedAt', desc: true }])
  const rows = useMemo(
    () =>
      tables.map((table) => {
        const member = members.find((candidate) => candidate.user_id === table.created_by_user_id)
        return {
          ...table,
          modifiedAt: table.updated_at ? Date.parse(table.updated_at) : undefined,
          creatorLabel:
            table.created_by_user_id === user?.uid ? 'Me' : member ? getMemberDisplayName(member) : membersLoaded ? kUnavailableMemberDisplayName : undefined,
        }
      }),
    [tables, members, membersLoaded, user?.uid]
  )
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Table Name',
        sortingFn: (a, b) => a.original.name.localeCompare(b.original.name) || a.id.localeCompare(b.id),
        meta: {
          headerClassName: 'pl-4 pr-3 text-left font-medium',
          cellClassName: 'py-3 pl-5 pr-4 [@container_(min-width:_640px)]:pr-8',
          sortLabels: { ascendingLabel: 'Sort A-Z', descendingLabel: 'Sort Z-A' },
        },
        cell: ({ row, table }) => (
          <div className="flex min-w-0 items-center gap-3">
            <TableIcon className="hidden size-[18px] shrink-0 text-brand-neutral-600 [@container_(min-width:_640px)]:block" aria-hidden="true" />
            <span className="truncate text-brand-neutral-950" title={row.original.name}>
              <SearchableText text={row.original.name} searchQuery={table.getState().globalFilter as string} />
            </span>
            <TableOriginTag documentAnalysis={row.original.document_analysis} />
          </div>
        ),
      }),
      columnHelper.accessor('modifiedAt', {
        header: 'Last Modified',
        sortUndefined: 'last',
        sortingFn: (a, b) => (a.original.modifiedAt ?? 0) - (b.original.modifiedAt ?? 0) || a.id.localeCompare(b.id),
        meta: {
          headerClassName: 'hidden pr-3 text-left font-medium [@container_(min-width:_640px)]:table-cell',
          cellClassName: 'hidden py-3 pl-1 pr-8 text-brand-neutral-800 [@container_(min-width:_640px)]:table-cell',
          sortLabels: { ascendingLabel: 'Sort old to new', descendingLabel: 'Sort new to old' },
          defaultSortDesc: true,
        },
        cell: ({ getValue }) => {
          const value = getValue()
          return value === undefined ? <span aria-label="Last modified unavailable">—</span> : <WorkProductTableUpdatedAt updatedAt={value} />
        },
      }),
      columnHelper.accessor('creatorLabel', {
        header: 'Created By',
        sortUndefined: 'last',
        sortingFn: (a, b) => (a.original.creatorLabel ?? '').localeCompare(b.original.creatorLabel ?? '') || a.id.localeCompare(b.id),
        meta: {
          headerClassName: 'pr-3 text-left font-medium',
          cellClassName: 'py-3 pl-1 pr-2',
          sortLabels: { ascendingLabel: 'Sort A-Z', descendingLabel: 'Sort Z-A' },
        },
        cell: ({ getValue, table }) => (
          <span className="block truncate text-brand-neutral-950" title={getValue()}>
            <SearchableText text={getValue() ?? ''} searchQuery={table.getState().globalFilter as string} />
          </span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        meta: { headerClassName: 'pr-3', cellClassName: 'py-3 pr-3' },
        cell: ({ row }) =>
          user?.uid === row.original.created_by_user_id ? (
            <div className="flex justify-end" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
              <TableActions tableId={row.id} name={row.original.name} label={`Actions for ${row.original.name}`} onChanged={onChanged} onDeleted={() => {}} />
            </div>
          ) : null,
      }),
    ],
    [user?.uid, onChanged]
  )
  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: { sorting, globalFilter: search },
    onSortingChange: setSorting,
    globalFilterFn: (row, _columnId, query: string) => {
      const text = `${row.original.name} ${row.original.creatorLabel ?? ''}`.toLowerCase()
      return query
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .every((term) => text.includes(term))
    },
  })
  const noResultsQuery = rows.length > 0 && table.getRowModel().rows.length === 0 && search.trim() ? search : null
  const active = sorting[0]
  const sortColumn = active?.id === 'name' ? 'Table Name' : active?.id === 'creatorLabel' ? 'Created By' : 'Last Modified'
  const sortDirection = active?.id === 'modifiedAt' ? (active.desc ? 'newest first' : 'oldest first') : active?.desc ? 'Z to A' : 'A to Z'
  return (
    <WorkProductTable
      table={table}
      onSortingChange={setSorting}
      onOpenRow={onOpen}
      isLoading={isLoading}
      caption="Tables in this matter"
      statusLabel={
        isLoading
          ? 'Loading tables'
          : noResultsQuery !== null
          ? 'No tables match your search'
          : rows.length > 0
          ? `Sorted by ${sortColumn}, ${sortDirection}`
          : ''
      }
      statusView={
        <WorkProductTableStatus
          isError={isError && rows.length === 0}
          onRetry={onRetry}
          noResultsQuery={noResultsQuery}
          onClearSearch={onClearSearch}
          resourceName="tables"
        >
          <div className="flex flex-col items-center justify-center bg-brand-neutral-50 p-8">
            <div className="flex w-full flex-col items-center gap-4 rounded-lg border border-brand-neutral-200 bg-white px-4 py-6">
              <TableIcon className="h-10 w-[62.5px] text-brand-600" aria-hidden="true" />
              <div className="flex max-w-[420px] flex-col items-center gap-0.5 text-center">
                <h2 className="text-base font-medium leading-6 text-brand-neutral-950">Create your first table</h2>
                <p className="text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">
                  Organize information from this matter in an editable table. Create one here or ask Paxton in chat.
                </p>
              </div>
              <Button size="sm" onClick={onCreate}>
                Create table
              </Button>
            </div>
          </div>
        </WorkProductTableStatus>
      }
    />
  )
}
